import { useState } from "react";
import { Check, FileText, Home, Users, Wallet } from "lucide-react";
import { THEME_OPCIONES, THEMES } from "@/lib/themes";
import { cn } from "@/lib/utils";
import { Aparece } from "./Aparece";
import { MARCA_PROPIA } from "./content";

// "Tu marca, tus colores": el visitante elige un color y la app de ejemplo se repinta con las
// MISMAS paletas que ofrece Configuración (`lib/themes.js`), no con colores inventados para la
// landing. Lo que ve acá es lo que su agencia puede elegir de verdad.
//
// Las clases salen de `THEMES` tal cual —`solidBg`, `activeItemBg`, `swatchBg`…— porque están
// escritas como literales en `lib/themes.js` y Tailwind las emite. Armarlas acá por concatenación
// las dejaría sin CSS (ver el comentario de `lib/themes.js`).

const NAV = [
  { icono: Home, label: "Panel" },
  { icono: Wallet, label: "Liquidaciones" },
  { icono: Users, label: "Personas" },
];

const AppConTema = ({ tema }) => {
  const t = THEMES[tema];
  return (
    <div className="overflow-hidden rounded-2xl bg-slate-50 shadow-xl shadow-slate-900/10 ring-1 ring-slate-900/5" aria-hidden="true">
      <div className={cn("flex h-11 items-center justify-between px-4 transition-colors duration-300", t.solidBg)}>
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-white/90 text-[9px] font-extrabold text-slate-700">LOGO</span>
          <span className="text-xs font-semibold text-white">Tu Corredora</span>
        </div>
        <span className="rounded-lg bg-white/10 px-2 py-1 text-[10px] font-semibold text-white/90 ring-1 ring-inset ring-white/15">Septiembre 2026</span>
      </div>
      <div className="grid grid-cols-[8.5rem_1fr] gap-3 p-3 sm:grid-cols-[10rem_1fr]">
        <nav className="space-y-1 rounded-xl bg-white p-2 ring-1 ring-inset ring-slate-200/60">
          {NAV.map((n, i) => {
            const Icono = n.icono;
            return (
              <div
                key={n.label}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-2 py-1.5 text-[11px] font-semibold transition-colors duration-300",
                  i === 1 ? cn(t.activeItemBg, t.activeItemText) : "text-slate-500",
                )}
              >
                <Icono className="h-3.5 w-3.5" /> {n.label}
              </div>
            );
          })}
        </nav>
        <div className="space-y-3">
          <div className="overflow-hidden rounded-xl bg-white ring-1 ring-inset ring-slate-200/60">
            <div className={cn("flex items-center justify-between px-3 py-2 transition-colors duration-300", t.cardHeaderBg)}>
              <span className="flex items-center gap-1.5 text-[11px] font-semibold text-white"><FileText className="h-3.5 w-3.5" />Liquidación · M. González</span>
              <span className="text-[10px] font-semibold text-white/80">PDF</span>
            </div>
            <div className="space-y-1.5 px-3 py-2.5 text-[11px]">
              <div className="flex justify-between"><span className="text-slate-500">Arriendo</span><span className="font-semibold tabular-nums text-slate-800">$756.686</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Honorarios</span><span className="font-semibold tabular-nums text-slate-800">−$60.535</span></div>
              <div className="flex justify-between border-t border-slate-100 pt-1.5"><span className="font-bold text-slate-800">Neto</span><span className="font-extrabold tabular-nums text-slate-900">$651.151</span></div>
            </div>
          </div>
          <div className={cn("rounded-xl px-3 py-2 text-center text-[11px] font-semibold text-white transition-colors duration-300", t.swatchBg)}>
            Enviar liquidaciones
          </div>
        </div>
      </div>
    </div>
  );
};

export const MarcaPropia = () => {
  const [tema, setTema] = useState("indigo");
  const opcion = THEME_OPCIONES.find((o) => o.key === tema);
  return (
    <section className="bg-slate-50" data-testid="landing-marca">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[1fr_1.1fr] lg:gap-16 lg:py-28">
        <Aparece>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">{MARCA_PROPIA.eyebrow}</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            {MARCA_PROPIA.titulo} <span className="text-slate-400">{MARCA_PROPIA.tituloTenue}</span>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600">{MARCA_PROPIA.texto}</p>

          <p className="mt-8 text-xs font-semibold uppercase tracking-widest text-slate-400">Prueba un color</p>
          <div className="mt-3 flex flex-wrap gap-2.5" role="radiogroup" aria-label="Color de tu marca">
            {THEME_OPCIONES.map((o) => {
              const activo = o.key === tema;
              return (
                <button
                  key={o.key}
                  type="button"
                  role="radio"
                  aria-checked={activo}
                  aria-label={o.label}
                  title={o.label}
                  onClick={() => setTema(o.key)}
                  className={cn(
                    "flex h-11 w-11 cursor-pointer items-center justify-center rounded-full ring-offset-2 ring-offset-slate-50 transition-shadow duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-indigo-200",
                    THEMES[o.key].swatchBg,
                    activo ? "ring-2 ring-slate-900" : "hover:ring-2 hover:ring-slate-300",
                  )}
                  data-testid={`marca-color-${o.key}`}
                >
                  {activo && <Check className="h-4 w-4 text-white" />}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-sm text-slate-500" aria-live="polite">
            <span className="font-semibold text-slate-700">{opcion?.label}</span> · {opcion?.description}
          </p>
        </Aparece>
        <Aparece delay={0.1}>
          <AppConTema tema={tema} />
        </Aparece>
      </div>
    </section>
  );
};
